import { Component } from 'react';
import type { ReactNode, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { observable, makeObservable, action } from 'mobx';
import { observer } from 'mobx-react';

import { wsService } from '../services/wsService';
import statsVisibilityService from '../services/statsVisibilityService';
import type { OllamaStats, ServerStatsWsPayload, ModelLoadingInfo } from '../models';
import { ImgFlash } from './Images/ImgFlash';
import { ImgCpu } from './Images/ImgCpu';
import { ImgGpu } from './Images/ImgGpu';
import { ImgRam } from './Images/ImgRam';
import { ImgTemperature } from './Images/ImgTemperature';
import { ImgPower } from './Images/ImgPower';
import { ImgDisk } from './Images/ImgDisk';
import { ImgInProgress } from './Images/ImgInProgress';

/** A stats sample with a receive timestamp so we can detect staleness. */
type ServerStatsEntry = {
  serverKey: string;
  stats: OllamaStats;
  modelLoading: ModelLoadingInfo | null;
  receivedAt: number;
};

/** After this long without a fresh sample the server is considered offline. */
const STALE_MS = 10_000;
/** Inference speeds (decode/prompt/acceptance) are considered outdated beyond this age. */
const INFER_STALE_MS = 5_000;

function fmtTps(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return '–';
  if (v >= 1000) return (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return Math.round(v).toString();
}

function fmtGb(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return '–';
  return (v / (1024 ** 3)).toFixed(1);
}

/** Format a byte size, switching to TB once the value exceeds 999 GB to save UI space. */
function fmtSize(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return '–';
  const gb = v / (1024 ** 3);
  if (gb > 999) return Math.round(v / (1024 ** 4)) + ' TB';
  return Math.round(gb) + ' GB';
}

function fmtUptime(sec: number | null | undefined): string {
  if (sec == null || !isFinite(sec) || sec < 0) return '–';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

@observer
class ServerStatusBadge extends Component {
  @observable private _stats: Map<string, ServerStatsEntry> = new Map();
  // Key of the server whose popup is open (opened by click, closed by outside click).
  @observable private _openKey: string | null = null;
  @observable private _isHidingPopup: boolean = false;
  // Incremented periodically so staleness ("offline") state re-renders without a new event.
  @observable private _forceTick = 0;
  // True on narrow (mobile) viewports where the header badge is hidden and, if enabled,
  // the badge is shown as a floating overlay over the chat instead.
  @observable private _isMobile = false;

  private _staleTimer: ReturnType<typeof setInterval> | null = null;
  private _mobileMq: MediaQueryList | null = null;
  private _popupHideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(props: {}) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    wsService.on('server:stats', this._onStats);
    // Force a re-render periodically so the "offline" state flips without a new event.
    this._staleTimer = setInterval(() => {
      this._tick();
    }, 5000);

    // Track the mobile breakpoint so the floating overlay toggles live on resize.
    this._mobileMq = window.matchMedia('(max-width: 768px)');
    this._setMobile(this._mobileMq.matches);
    this._mobileMq.addEventListener('change', this._onMobileChange);
    document.addEventListener('click', this._onDocumentClick);
  }

  componentWillUnmount() {
    wsService.off('server:stats', this._onStats);
    if (this._staleTimer !== null) {
      clearInterval(this._staleTimer);
      this._staleTimer = null;
    }
    if (this._mobileMq) {
      this._mobileMq.removeEventListener('change', this._onMobileChange);
      this._mobileMq = null;
    }
    document.removeEventListener('click', this._onDocumentClick);
    if (this._popupHideTimeout !== null) {
      clearTimeout(this._popupHideTimeout);
      this._popupHideTimeout = null;
    }
  }

  @action
  private _setMobile(isMobile: boolean) {
    this._isMobile = isMobile;
  }

  private _onMobileChange = (e: MediaQueryListEvent) => {
    this._setMobile(e.matches);
  };

  @action
  private _onStats = (data: unknown) => {
    const payload = data as ServerStatsWsPayload;
    if (!payload || !payload.serverKey || !payload.stats) return;
    this._stats.set(payload.serverKey, {
      serverKey: payload.serverKey,
      stats: payload.stats,
      modelLoading: payload.modelLoading ?? null,
      receivedAt: Date.now(),
    });
  };

  private _isStale(entry: ServerStatsEntry): boolean {
    return Date.now() - entry.receivedAt > STALE_MS;
  }

  @action
  private _tick() {
    this._forceTick++;
  }

  /** Toggle the popup for a server on click; clicking an already-open one closes it. */
  @action
  private _onClick = (e: MouseEvent, key: string) => {
    e.stopPropagation();
    if (this._openKey === key) {
      this._closePopup();
    } else {
      if (this._popupHideTimeout !== null) {
        clearTimeout(this._popupHideTimeout);
        this._popupHideTimeout = null;
      }
      this._isHidingPopup = false;
      this._openKey = key;
    }
  };

  @action
  private _closePopup() {
    if (!this._openKey || this._isHidingPopup) return;
    this._isHidingPopup = true;
    this._popupHideTimeout = setTimeout(action(() => {
      this._openKey = null;
      this._isHidingPopup = false;
      this._popupHideTimeout = null;
    }), 150);
  }

  @action
  private _onDocumentClick = () => {
    if (this._openKey) this._closePopup();
  };

  private _renderPill(entry: ServerStatsEntry, stale: boolean) {
    const ollama = entry.stats.ollama;
    const status = ollama?.status ?? 'idle';

    if (stale) {
      return (
        <span className="ssb-pill ssb-pill--offline" title="No stats received">
          <span className="ssb-pill-dot ssb-pill-dot--offline" />
          offline
        </span>
      );
    }

    if (status === 'decode') {
      const tps = ollama?.decodeTps ?? ollama?.decode3sTps ?? null;
      return (
        <span className="ssb-pill ssb-pill--decode" title="Decoding (generating)">
          <span className="ssb-pill-dot ssb-pill-dot--decode" />
          <ImgFlash size={13} />
          <span className="ssb-pill-value">{fmtTps(tps)} t/s</span>
        </span>
      );
    }

    if (status === 'prompt') {
      const tps = ollama?.promptTps ?? null;
      const pct = ollama?.promptPct;
      return (
        <span className="ssb-pill ssb-pill--prompt" title="Prompt (reading context)">
          <span className="ssb-pill-dot ssb-pill-dot--prompt" />
          <ImgCpu size={13} />
          <span className="ssb-pill-value">{fmtTps(tps)} t/s</span>
          {pct != null && <span className="ssb-pill-pct">{Math.round(pct)}%</span>}
        </span>
      );
    }

    if (entry.modelLoading) {
      const loading = entry.modelLoading;
      const pct = this._loadPct(loading);
      const label = this._shortModelName(loading.model);
      return (
        <span className="ssb-pill ssb-pill--loading" title={`Loading ${loading.model}`}>
          <span className="ssb-pill-dot ssb-pill-dot--loading" />
          <ImgInProgress size={13} />
          <span className="ssb-pill-value">{label}</span>
          <span className="ssb-pill-pct">{pct}%</span>
        </span>
      );
    }

    // No inference / loading, but the GPU is busy — show GPU usage instead of "idle".
    const gpuUsage = entry.stats.gpu?.usage;
    if (status === 'idle' && gpuUsage != null && gpuUsage > 10) {
      return (
        <span className="ssb-pill ssb-pill--gpu" title="GPU busy (no inference tracked)">
          <span className="ssb-pill-dot ssb-pill-dot--gpu" />
          <ImgGpu size={13} />
          <span className="ssb-pill-value">{Math.round(gpuUsage)}%</span>
        </span>
      );
    }

    return (
      <span className="ssb-pill ssb-pill--idle" title="Idle">
        <span className="ssb-pill-dot ssb-pill-dot--idle" />
        idle
      </span>
    );
  }

  /** Real bytes-based load progress when published by the server; falls back to a time estimate. */
  private _loadPct(loading: ModelLoadingInfo): number {
    if (loading.loadPct != null) return Math.min(100, Math.max(0, loading.loadPct));
    return this._estimateLoadPct(loading);
  }

  /** Coarse progress estimate for a model load, based on elapsed time (capped at 100%). */
  private _estimateLoadPct(loading: ModelLoadingInfo): number {
    const elapsedMs = Date.now() - loading.startedAt;
    // Model loads typically take a few seconds to a couple of minutes; approximate 100% at ~120s.
    const pct = Math.min(100, Math.max(3, (elapsedMs / 120_000) * 100));
    return Math.round(pct);
  }

  /** Shorten a model name for the narrow badge (strip registry/version noise). */
  private _shortModelName(model: string): string {
    const base = model.split('/').pop() ?? model;
    return base.length > 12 ? base.slice(0, 11) + '…' : base;
  }

  private _renderRow(label: ReactNode, value: string, barPct: number | null, barColor: string, outdated = false) {
    return (
      <div className={'ssb-row' + (outdated ? ' ssb-row--outdated' : '')}>
        <span className="ssb-row-label">{label}</span>
        {barPct != null ? (
          <span className="ssb-row-bar-wrap">
            <span
              className="ssb-row-bar"
              style={{ width: `${Math.max(0, Math.min(100, barPct))}%`, background: barColor }}
            />
          </span>
        ) : (
          <span className="ssb-row-bar-wrap ssb-row-bar-wrap--empty" />
        )}
        <span className="ssb-row-value">{value}</span>
      </div>
    );
  }

  private _renderPopup(entry: ServerStatsEntry) {
    const s = entry.stats;
    const ollama = s.ollama;
    const gpu = s.gpu;

    const acc = ollama?.draftAcceptance;
    const accPct = acc != null ? Math.round(acc * 100) : null;
    // Inference speeds reflect the last parsed activity; beyond INFER_STALE_MS they are stale.
    // Age is computed on the proxy machine (single clock) so browser/proxy clock skew can't
    // make the rows appear permanently outdated.
    const inferStale = ollama?.lastUpdateAgeMs != null
      ? ollama.lastUpdateAgeMs > INFER_STALE_MS
      : false;

    let modelName = entry.modelLoading?.model ?? '???';
    if (modelName.length > 12) {
      modelName = modelName.slice(0, 12) + '…';
    }

    return (
      <div className={`ssb-popup${this._isHidingPopup ? ' ssb-popup--hiding' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="ssb-popup-title">Server · {entry.serverKey}</div>

        {/* Inference */}
        <div className="ssb-popup-section">Inference</div>
        {this._renderRow('Status', ollama?.status ?? 'idle', null, '', inferStale)}
        {this._renderRow('Decode', `${fmtTps(ollama?.decodeTps ?? ollama?.decode3sTps)} t/s`, null, '', inferStale)}
        {this._renderRow('Prompt', `${fmtTps(ollama?.promptTps)} t/s`,
          ollama?.promptPct != null ? Math.min(100, ollama!.promptPct!) : null, 'var(--accent-color, #4a9eff)', inferStale)}
        {accPct != null && this._renderRow('Draft accept', `${accPct}%`, accPct, 'var(--success-color)', inferStale)}
        {entry.modelLoading && this._renderRow(<><ImgInProgress size={11} /> Loading</>, modelName,
          this._loadPct(entry.modelLoading), 'var(--warning-color, #eab308)')}

        {/* GPU */}
        {gpu && (
          <>
            <div className="ssb-popup-section">GPU</div>
            {this._renderRow(<><ImgGpu size={11} /> Load</>, `${Math.round(gpu.usage)}%`, gpu.usage, 'var(--accent-color, #4a9eff)')}
            {this._renderRow(<><ImgRam size={11} /> VRAM</>, `${fmtGb(gpu.memoryUsed)} / ${fmtGb(gpu.memoryTotal)} GB`,
              gpu.memoryTotal ? (gpu.memoryUsed / gpu.memoryTotal) * 100 : null, 'var(--accent-color)')}
            {this._renderRow(<><ImgTemperature size={11} /> Temp</>, `${Math.round(gpu.temperature)}°C`, null, '')}
            {this._renderRow(<><ImgPower size={11} /> Power</>, `${Math.round(gpu.powerConsumption)} W`, null, '')}
          </>
        )}

        {/* System */}
        <div className="ssb-popup-section">System</div>
        {this._renderRow('CPU', `${Math.round(s.cpu?.usage ?? 0)}%`, s.cpu?.usage ?? 0, 'var(--warning-color, #eab308)')}
        {this._renderRow('RAM', `${fmtGb(s.memory?.used)} / ${fmtGb(s.memory?.total)} GB`,
          s.memory?.total ? (s.memory!.used / s.memory!.total) * 100 : null, 'var(--accent-color)')}
        {s.disk && this._renderRow(<><ImgDisk size={11} /> Disk</>, `${fmtSize(s.disk.free)} / ${fmtSize(s.disk.size)}`,
          s.disk.size ? (s.disk.used / s.disk.size) * 100 : null, 'var(--text-muted)')}
        {this._renderRow('Uptime', fmtUptime(s.uptime), null, '')}
      </div>
    );
  }

  render() {
    // Read so MobX re-renders on periodic ticks (for staleness / offline detection).
    void this._forceTick;
    // Server stats are an admin-only feed; never render the badge for non-admins.
    if (window.user?.role !== 'admin') return null;
    const entries = Array.from(this._stats.values());
    if (entries.length === 0) return null;

    // On mobile the header badge is hidden (display:none), so the floating overlay is
    // portaled out of the hidden button. It's attached to .main-content (not body) so that
    // when the mobile sidebar opens, the same `filter: blur(4px)` blurs the stats too and
    // the stats stay inside main-content's stacking context — i.e. below the sidebar.
    // Tapping a pill opens the same details popup as on desktop.
    if (this._isMobile) {
      if (!statsVisibilityService.mobileVisible) return null;
      const host = document.querySelector<HTMLElement>('.main-content');
      if (!host) return null;
      return createPortal(
        <div className="ssb-float">
          {entries.map(entry => this._renderItem(entry))}
        </div>,
        host
      );
    }

    return (
      <div className="ssb-container">
        {entries.map(entry => this._renderItem(entry))}
      </div>
    );
  }

  private _renderItem(entry: ServerStatsEntry) {
    const stale = this._isStale(entry);
    const open = this._openKey === entry.serverKey;
    return (
      <div
        key={entry.serverKey}
        className="ssb-item"
        onClick={e => this._onClick(e, entry.serverKey)}
      >
        {this._renderPill(entry, stale)}
        {open && !stale && this._renderPopup(entry)}
      </div>
    );
  }
}

export default ServerStatusBadge;
